import { Capacitor } from '@capacitor/core';

import { NativeDebugLog } from '../plugins/nativeDebugLog';

const MAX_MESSAGE_LENGTH = 8000;
const MAX_SERIALIZED_DEPTH = 3;
const MAX_SERIALIZED_KEYS = 30;
const MAX_SERIALIZED_ITEMS = 30;
const MAX_STRING_LENGTH = 600;

type ConsoleLevel = 'log' | 'debug' | 'info' | 'warn' | 'error';

let nativeDebugEnabledPromise: Promise<boolean> | null = null;

const isNativeDebugBridgeSupported = (): boolean =>
  Capacitor.isNativePlatform() && ['android', 'ios'].includes(Capacitor.getPlatform());

const summarizeValue = (
  value: unknown,
  depth = 0,
  seen = new WeakSet<object>(),
): unknown => {
  if (typeof value === 'string') {
    return value.length > MAX_STRING_LENGTH ? `${value.slice(0, MAX_STRING_LENGTH)}... [${value.length} chars]` : value;
  }

  if (value === null || value === undefined || typeof value !== 'object') {
    return value;
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }

  if (seen.has(value)) {
    return '[Circular]';
  }
  seen.add(value);

  if (depth >= MAX_SERIALIZED_DEPTH) {
    return Array.isArray(value) ? `[Array(${value.length})]` : `[Object(${Object.keys(value).length})]`;
  }

  if (Array.isArray(value)) {
    const items = value.slice(0, MAX_SERIALIZED_ITEMS).map((item) => summarizeValue(item, depth + 1, seen));
    if (value.length > MAX_SERIALIZED_ITEMS) {
      items.push(`... ${value.length - MAX_SERIALIZED_ITEMS} more items`);
    }
    return items;
  }

  const summarized: Record<string, unknown> = {};
  const entries = Object.entries(value);
  entries.slice(0, MAX_SERIALIZED_KEYS).forEach(([key, entryValue]) => {
    summarized[key] = summarizeValue(entryValue, depth + 1, seen);
  });
  if (entries.length > MAX_SERIALIZED_KEYS) {
    summarized.__truncatedKeys = entries.length - MAX_SERIALIZED_KEYS;
  }
  return summarized;
};

const stringifyArg = (value: unknown): string => {
  if (value === null) {
    return 'null';
  }

  if (value === undefined) {
    return 'undefined';
  }

  if (typeof value === 'string') {
    return value;
  }

  try {
    if (typeof value === 'object') {
      return JSON.stringify(summarizeValue(value));
    }
    return String(value);
  } catch {
    return String(value);
  }
};

const normalizeMessage = (args: unknown[]): string =>
  args
    .map((arg) => stringifyArg(arg))
    .join(' ')
    .slice(0, MAX_MESSAGE_LENGTH);

const isNativeDebugEnabled = async (): Promise<boolean> => {
  if (!isNativeDebugBridgeSupported()) {
    return false;
  }

  if (!nativeDebugEnabledPromise) {
    nativeDebugEnabledPromise = NativeDebugLog.getStatus()
      .then((status) => Boolean(status.enabled))
      .catch(() => false);
  }

  return nativeDebugEnabledPromise;
};

export const mirrorConsoleToNative = (level: ConsoleLevel, args: unknown[]): void => {
  if (!isNativeDebugBridgeSupported()) {
    return;
  }

  const message = normalizeMessage(args);
  if (!message) {
    return;
  }

  void (async () => {
    if (!(await isNativeDebugEnabled())) {
      return;
    }

    await NativeDebugLog.write({
      level,
      message,
    });
  })().catch(() => {
    // Swallow bridge failures to avoid recursive console noise inside the logger bridge.
  });
};
